import { Link } from "react-router-dom";
import { CategoryProduct } from "@shared/types/CategoryTypes.interface";
import { ProductPriceResponse } from "@shared/helpers/apis/fetchProductPrices";
import ImageWithFallback from "@shared/components/Molecules/ImageWithFallback/ImageWithFallback.component";
import { StockStatus } from "@shared/components/Molecules/StockStatus/StockStatus.component";
import QuantityPicker from "@shared/components/Molecules/QuantityPicker/QuantityPicker.component";
import { useBasket } from "@shared/context/BasketContext";
import { useToast } from "@shared/context/ToastContext";
import { useTranslations } from "@shared/context/useTranslations";
import { useFavoriteManagement } from "@shared/hooks/useFavoriteManagement";
import { FavoritesModal } from "@shared/components/Molecules/FavoritesModal/FavoritesModal.component";
import { formatNumber } from "@shared/helpers/formatPrice";
import { getThumbnailImageUrl } from "@shared/helpers/apis/productHelper";
import { getRoutePathByPageType } from "@shared/helpers/findPathForSystemSite";
import { IconNames } from "@shared/enums/icons.enums";
import Button from "@shared/components/Atoms/Button/Button";
import { SvgIcon } from "@shared/components/Atoms/SvgIcon/SvgIcon";
import S from "./ReplacementProductCard.module.scss";
import { useCrane } from "@shared/context/CraneContext";

interface ReplacementProductCardProps {
    product: CategoryProduct;
    prices?: ProductPriceResponse;
    pricesLoaded: boolean;
    quantity: number;
    onQuantityChange: (quantity: number) => void;
}

export const ReplacementProductCard = ({
    product,
    prices,
    pricesLoaded,
    quantity,
    onQuantityChange,
}: ReplacementProductCardProps) => {
    const { addToBasket } = useBasket();
    const { showToast } = useToast();
    const { getTranslation } = useTranslations();
    const { craneInfo } = useCrane();
    // @ts-expect-error
    const currentCulture = globalThis?.currentCulture || "en-gb";
    const basketPath = getRoutePathByPageType("Basket") || "/basket";

    const fav = useFavoriteManagement(product);
    const { isProductFavorited, handleFavoriteClick } = fav;

    const isReplacementUnavailable = product.lifeCycle?.isUnavailable ?? false;
    const isReplacementDkOwned = product.isDkOwned || product.productOwner?.toUpperCase() === "DK";
    const hasPrice = !isReplacementUnavailable && pricesLoaded && !!prices?.discountedPrice && prices.discountedPrice > 0;
    const hasDiscount = hasPrice && !!prices?.discountPerUnit && prices.discountPerUnit > 0;
    const hasDiscountBadge = !isReplacementUnavailable && prices?.discountTable?.hasQuantityDiscount;

    const handleAddToBasket = () => {
        addToBasket({
            productId: product.id.toString(),
            quantity,
            data: { url: product.images[0]?.url },
        });
        showToast(
            <>
                {quantity} {getTranslation("general.piece")} {product.name}{" "}
                {getTranslation("general.toastMessage.addedTo")}{" "}
                <Link to={basketPath}>{getTranslation("general.toastMessage.yourBasket")}</Link>
            </>
        );
    };

    return (
        <div className={S.card}>
            {/* Top-left: image */}
            <Link className={S.imageWrapper} to={product.url?.urlPath || ""}>
                <ImageWithFallback
                    src={getThumbnailImageUrl(product.images[0]?.url)}
                    showFallback={!product.images?.[0]?.url}
                    alt={product.name}
                    className={S.image}
                />
            </Link>

            {/* Top-right: id / name / description / discount / heart */}
            <div className={S.infoCol}>
                <div className={S.topRow}>
                    <Link to={product.url?.urlPath || ""} className={S.productId}>
                        {product.id}
                    </Link>
                    <div className={S.badges}>
                        {hasDiscountBadge && (
                            <span className={S.discountBadge}>
                                <SvgIcon iconName={IconNames.Discount} />
                            </span>
                        )}
                        {(!isReplacementUnavailable || isProductFavorited) && (
                            <button
                                className={S.favoriteBtn}
                                onClick={handleFavoriteClick}
                                aria-label={getTranslation("button.toFavorites")}
                            >
                                <SvgIcon iconName={isProductFavorited ? IconNames.Heartfull : IconNames.Heart} />
                            </button>
                        )}
                    </div>
                </div>
                <div className={S.nameDesc}>
                    <span className={S.productName}>{product.name}</span>
                    {product.shortDescription && (
                        <span className={S.shortDescription}>{product.shortDescription}</span>
                    )}
                </div>
            </div>

            {/* Bottom-left: stock */}
            <div className={S.stockCol}>
                {!isReplacementUnavailable && (
                    <StockStatus stock={product.stock} />
                )}
            </div>

            {/* Bottom-right: prices + qty + buy */}
            <div className={S.pricesActions}>
                {isReplacementUnavailable ? (
                    <span className={S.unavailableBadge}>{getTranslation("product.unavailable")}</span>
                ) : (
                    <>
                        <div className={S.prices}>
                            {hasPrice ? (
                                <>
                                    {hasDiscount && (
                                        <span className={S.listPrice}>
                                            {formatNumber(prices!.listPrice, currentCulture, {
                                                decimals: 2,
                                            })}{" "}
                                            {prices!.currencyCode?.toUpperCase()}
                                        </span>
                                    )}
                                    {hasDiscount && (
                                        <span className={S.discount}>
                                            -{formatNumber(prices!.discountPerUnit, currentCulture, {
                                                decimals: 2,
                                            })}{" "}
                                            {prices!.currencyCode?.toUpperCase()}
                                        </span>
                                    )}
                                    <span className={S.actualPrice}>
                                        {formatNumber(prices!.discountedPrice, currentCulture, {
                                            decimals: 2,
                                        })}{" "}
                                        {prices!.currencyCode?.toUpperCase()}
                                    </span>
                                </>
                            ) : (
                                !pricesLoaded && <span className={S.loadingPrice}>…</span>
                            )}
                        </div>

                        <div className={S.actions}>
                            {isReplacementDkOwned && (
                                <a
                                    className={S.contactBtn}
                                    href={`mailto:${getTranslation("crane.afterSales.email")}?subject=${encodeURIComponent(getTranslation("crane.afterSales.subject", { serial: craneInfo?.serialNumber || "", craneName: craneInfo?.craneName || "" }))}&body=${encodeURIComponent(getTranslation("crane.afterSales.body", { id: product.id || "", productName: product.name || "" }))}`}
                                >
                                    <SvgIcon iconName={IconNames.Mail} />
                                    {getTranslation("crane.afterSales.contact")}
                                </a>
                            )}
                            {!isReplacementDkOwned && (
                                <QuantityPicker quantity={quantity} setQuantity={onQuantityChange} />
                            )}
                            {!isReplacementDkOwned && hasPrice && (
                                <Button
                                    className={S.buyBtn}
                                    variant="primary"
                                    iconName={IconNames.Basket}
                                    iconPlacement="left"
                                    onClick={handleAddToBasket}
                                >
                                    {getTranslation("button.buy")}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <FavoritesModal controller={fav} />
        </div>
    );
};
